import React from "react";

import { css } from "@emotion/react";

import { HeaderSpacer } from "../_layout-helpers/spacers";
import { Spacer } from "../../components/_shared-components/arrangement/Space";
import { ContentContainer } from "../../components/_shared-components/arrangement/Container";
import { HeaderComponent } from "../../components/HeaderComponent/HeaderComponent";
import { FooterComponent } from "../../components/FooterComponent/FooterComponent";
import { PageMetadataComponent } from "../../components/PageMetadataComponent/PageMetadataComponent";
import { ExtractSwarovskiPageData, PressReleasePageData } from "@swo/storefront";
import {
  PageHeadline,
  PageHeadlineMedium,
  PageMiniHeadline,
} from "../../components/_shared-components/ui-elements/PageHeadline";
import { IconsAndTextsItem } from "../../components/IconsAndTextsComponent/IconsAndTextsItem";
import { PressReleaseDateLabel } from "./shared/PressReleaseDateLabel";
import { CSS_MQ } from "../../styles/breakpoints";
import { CSS_COLORS, CSS_SPACER_SIZES } from "../../styles/styles-css-variables";
import { options } from "./shared/rich-text-options";
import { fontColor, FONTS, fontSize } from "../../styles/fonts";
import { useStore } from "../../store/hooks/use-store";
import { plainTextStyles } from "../../styles/text-styles";
import { EXPERIMENTAL_TextWithProductNameRenderer } from "../../domain/swarovski-optik/products/product-name/product-name-renderer";
import { SORichTextRenderer } from "../../components/_shared-components/so-rich-text";
import { ComponentRenderer } from "../_layout-helpers/component-renderer";
import { ReactComponent as DownloadPressReleaseSvg } from "../../assets/icons/download-press-release.svg";
import { t2 } from "../../domain/translations/translations";
import { T } from "../../components/T";

export const PressReleasePageLayout: React.FC<ExtractSwarovskiPageData<PressReleasePageData>> = props => {
  const { locale } = props;
  const { locationOrigin } = useStore().state;
  const gt = t2(props.generalTranslations);

  return (
    <>
      <PageMetadataComponent
        {...props.data.metadata}
        locationOrigin={locationOrigin}
        languageIsoCode={props.locale.language.isoCode}
      />

      <HeaderComponent header={props.data.header} version={"normal"} />
      <HeaderSpacer />

      <ContentContainer>
        <Spacer size={"md"} />
        <PageHeadlineMedium align={"center"}>
          <T>{props.data.pageHeadline}</T>
        </PageHeadlineMedium>
        <Spacer size={"lg"} />

        <div
          css={css`
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 50%));
            grid-column-gap: 20px;

            > .headline {
              grid-column: 1 / span 2;
              ${CSS_MQ.atLeastMd} {
                max-width: 66%;
              }
            }
          `}
        >
          <span
            className={"headline"}
            css={css`
              display: flex;
              flex-direction: column;

              .date {
                margin-bottom: 20px;
              }
            `}
          >
            <PressReleaseDateLabel dateStr={props.data.pressRelease.date} locale={locale} className={"date"} />

            <PageMiniHeadline>
              <EXPERIMENTAL_TextWithProductNameRenderer text={props.data.pressRelease.subheadline} />
            </PageMiniHeadline>
            <PageHeadline
              align={"left"}
              css={css`
                margin-top: 0;
              `}
            >
              <EXPERIMENTAL_TextWithProductNameRenderer text={props.data.pressRelease.headline} />
            </PageHeadline>
          </span>

          <main
            css={css`
              ${CSS_MQ.atMostSm} {
                grid-column: span 2;
              }

              ${FONTS.SwarovskiFutura300};
              ${fontColor.black};

              ${plainTextStyles};

              max-width: 100%;
              ${CSS_MQ.atLeastMd} {
                max-width: 36em;
              }

              word-break: break-word;

              a {
                ${FONTS.SwarovskiFutura500};
                ${fontSize.x2};
                color: ${CSS_COLORS.Green};
                text-transform: uppercase;
              }

              > * {
                margin-top: 0;
              }

              > * + * {
                margin-top: initial;
              }

              > *:last-child {
                margin-bottom: 0;
              }

              h2 {
                font-weight: 300;
              }

              h2 ~ h2 {
                margin-top: 50px !important;
              }

              h3,
              h4 {
                font-weight: 500;
                line-height: 1.15;
              }

              .quotation-block {
                margin-top: ${CSS_SPACER_SIZES.md};
                margin-bottom: ${CSS_SPACER_SIZES.md};

                max-width: 100%;
              }
            `}
          >
            <SORichTextRenderer richText={props.data.pressRelease.richText} options={options} />
          </main>

          <div
            css={css`
              position: relative;

              ${CSS_MQ.atMostSm} {
                grid-column: span 2;
                margin-top: 50px;
              }
            `}
          >
            <div
              css={css`
                ${CSS_MQ.atLeastMd} {
                  max-width: 25em;
                }

                display: flex;
                flex-direction: column;
                text-align: center;
                margin-right: 0;
                margin-left: auto;

                position: sticky;
                top: 100px;
              `}
            >
              <IconsAndTextsItem
                ctaButton={props.data.downloadArticle.ctaButton}
                headline={<T>{props.data.downloadArticle.headline}</T>}
                text={props.data.downloadArticle.text}
                icon={<DownloadPressReleaseSvg title={"Download icon"} />}
              />
              <Spacer size={"md"} />
              {props.data.mediaLibrary && (
                <IconsAndTextsItem
                  headline={<T>{props.data.mediaLibrary.headline}</T>}
                  icon={props.data.mediaLibrary.icon}
                  text={props.data.mediaLibrary.text}
                  ctaButton={props.data.mediaLibrary.ctaButton}
                />
              )}
            </div>
          </div>
        </div>
      </ContentContainer>

      <Spacer size={"md"} />

      <ComponentRenderer components={props.data.components} localeForNumberFormat={props.locale} translations={gt} />

      <Spacer size={"md"} />
      <FooterComponent {...props.data.footer} />
    </>
  );
};
